/**
 * GRN (Goods Receipt Note) Validation
 * Centralized validation messages, rules, and helper functions
 */

// ============================================
// ERROR MESSAGES - FORM VALIDATION
// ============================================
export const GRN_ERROR_MESSAGES = {
  // Supplier
  SUPPLIER_REQUIRED: "Please select a supplier before submitting.",
  SUPPLIER_NOT_FOUND: "Supplier not found.",

  // GRN Details
  WAREHOUSE_REQUIRED: "Warehouse/Location is required",
  DELIVERY_DATE_REQUIRED: "Delivery date is required",
  DELIVERY_LOCATION_REQUIRED: "Delivery Location is Required.",
  INVOICE_NUMBER_REQUIRED: "Invoice Number is Required.",
  INVOICE_DATE_REQUIRED: "Invoice Date is Required.",
  PARTIAL_SHIPMENTS_REQUIRED: "Partial Shipments is Required.",

  // Items
  NO_ITEMS: "Please add at least one item before submitting.",
  ITEM_REQUIRED: "At least one item is required",

  // Selection
  SELECT_AT_LEAST_ONE_GRN: "Please select at least one GRN",
  SELECT_SAME_STATUS: "Please select GRNs with the same status",

  // Required Fields
  ALL_FIELDS_REQUIRED: "Please fill the all the required fields",
  REQUIRED_FIELD: "This field is required",
  REASON_REQUIRED: "Please select a reason before confirming.",

  // Item Field Validation
  BATCH_NUMBER_REQUIRED: "Batch Number is Required.",
  MFD_REQUIRED: "MFD is Required.",
  ACCEPTED_QTY_REQUIRED: "Accepted Quantity is Required",
  PO_QTY_REQUIRED: "PO Quantity is Required.",
  RECEIVED_QTY_REQUIRED: "Received Quantity is Required.",
  UNIT_PRICE_REQUIRED: "Unit Price is Required.",
  RATE_REQUIRED: "Rate is required",

  // Quantity Validation
  REJECT_QTY_EXCEEDS_PO: "Reject quantity cannot exceed PO quantity",
  INVALID_QUANTITY: "Please enter a valid quantity",
  RETURN_QTY_REQUIRED: "Please add a return quantity for at least one item",

  // Amount Validation
  INVALID_AMOUNT: "Please enter a valid amount",
  INVALID_NUMBER: "Enter a valid number (max 2 decimals)",
  RATE_REQUIRED_BEFORE_ADD: "Please enter a rate before adding.",

  // Charge Validation
  CHARGE_ALREADY_ADDED: " already added.", // Prefix with charge type name

  // File Upload
  FILE_SIZE_EXCEEDED: "File size exceeds the maximum limit of 5MB",
  INVALID_FILE_TYPE: "Invalid file type",

  // Return
  MISSING_CRITICAL_DATA: "Missing critical GRN data. Please refresh and try again.",
  RETURN_NO_PRODUCTS: "No products to return.",
};

// ============================================
// SUCCESS MESSAGES
// ============================================
export const GRN_SUCCESS_MESSAGES = {
  // Create
  CREATE_SUCCESS: "GRN Created Successfully!",
  SAVE_SUCCESS: "GRN Saved Successfully!",
  HOLD_SUCCESS: "GRN Action Completed",
  SUBMIT_SUCCESS: "GRN Submitted Successfully!",

  // Update
  UPDATE_SUCCESS: "GRN Updated Successfully!",
  APPROVE_SUCCESS: "GRN Approved Successfully!",
  REJECT_SUCCESS: "GRN Rejected Successfully!",

  // Delete
  DELETE_SUCCESS: "Item Deleted Successfully!",

  // Return
  RETURN_SAVE_SUCCESS: "Products to be returned have been saved",
  RETURN_SUCCESS: "GRN Returned Successfully!",
  RETURN_APPROVE_SUCCESS: "Return GRN Approved Successfully!",
};

// ============================================
// FAILURE MESSAGES
// ============================================
export const GRN_FAILURE_MESSAGES = {
  // Create
  CREATE_FAILED: "Failed to create GRN. Please try again.",
  SAVE_FAILED: "Failed to save GRN. Please try again.",
  HOLD_FAILED: "Failed to hold GRN. Please try again.",
  SUBMIT_FAILED: "Failed to submit GRN. Please try again.",

  // Update
  UPDATE_FAILED: "Failed to update GRN. Please try again.",
  APPROVE_FAILED: "Failed to approve GRN. Please try again.",
  REJECT_FAILED: "Failed to reject GRN. Please try again.",

  // Delete
  DELETE_FAILED: "Failed to Delete Item. Please try again.",

  // Return
  RETURN_FAILED: "Failed to return GRN. Please try again.",

  // Generic
  OPERATION_FAILED: "Operation failed. Please try again.",
  NETWORK_ERROR: "Network error. Please check your connection.",
  INVALID_RESPONSE: "Invalid response from server",
  SAVE_FAILED_PREFIX: "Failed to save:",
  FETCH_GRN_FAILED: "Failed to fetch GRN data",
};

// ============================================
// CONFIRMATION MESSAGES
// ============================================
export const GRN_CONFIRM_MESSAGES = {
  CANCEL_UNSAVED: "Are you sure you want to cancel? Unsaved changes will be lost.",
  LEAVE_UNSAVED: "If you leave, Unsaved changes will be lost.",
  SAVE_GRN: "Are you sure you want to save this GRN?",
  SUBMIT_GRN: "Are you sure you want to submit this GRN?",
  HOLD_GRN: "Are you sure you want to hold this GRN?",
  DELETE_ITEM: "Are you sure you want to delete this item?",
  APPROVE_GRN: "Are you sure you want to approve this GRN?",
  REJECT_GRN: "Are you sure you want to reject this GRN?",
  RETURN_GRN: "Are you sure you want to return this GRN Items?",
  HOLD_GRN_WARNING: "Confirm hold GRN? This will stop the GRN from being processed. Do you want to continue?",
};

// ============================================
// VALIDATION RULES
// ============================================
export const VALIDATION_RULES = {
  // Text Length
  MIN_LENGTH: {
    DEFAULT: 1,
    NAME: 3,
    CODE: 2,
  },
  MAX_LENGTH: {
    DEFAULT: 255,
    NAME: 100,
    CODE: 50,
    REMARK: 500,
  },

  // Number Ranges
  PERCENTAGE: {
    MIN: 0,
    MAX: 100,
    DECIMALS: 2,
  },
  AMOUNT: {
    MIN: 0,
    MAX: 999999999.99,
    DECIMALS: 2,
  },
  QUANTITY: {
    MIN: 0,
    MAX: 999999,
    DECIMALS: 2,
  },

  // File Upload
  FILE_SIZE: {
    MAX: 5 * 1024 * 1024, // 5MB
    MAX_MB: 5,
  },
  FILE_TYPES: {
    IMAGE: ["image/jpeg", "image/jpg", "image/png", "image/gif"],
    DOCUMENT: ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
  },
};

// ============================================
// REGEX PATTERNS FOR VALIDATION
// ============================================
export const VALIDATION_REGEX = {
  // Numbers with decimals
  PERCENTAGE: /^\d{0,3}(\.\d{0,2})?$/, // Max 3 digits, 2 decimals
  AMOUNT: /^\d{0,9}(\.\d{0,2})?$/, // Max 9 digits, 2 decimals
  POSITIVE_NUMBER: /^\d+(\.\d{0,2})?$/, // Any positive number with 2 decimals
  INTEGER: /^\d+$/, // Integers only

  // Text patterns
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
  ALPHANUMERIC_SPACE: /^[a-zA-Z0-9\s]+$/,
  NO_SPECIAL_CHARS: /^[a-zA-Z0-9\s\-_]+$/,
};

// ============================================
// VALIDATION HELPER FUNCTIONS
// ============================================

/**
 * Validate if a number matches percentage pattern
 * @param {string|number} value - Value to validate
 * @returns {boolean} - True if valid
 */
export const isValidPercentage = (value) => {
  if (value === "" || value === null || value === undefined) return true;
  const num = parseFloat(value);
  return (
    VALIDATION_REGEX.PERCENTAGE.test(value) &&
    num >= VALIDATION_RULES.PERCENTAGE.MIN &&
    num <= VALIDATION_RULES.PERCENTAGE.MAX
  );
};

/**
 * Validate if a number matches amount pattern
 * @param {string|number} value - Value to validate
 * @returns {boolean} - True if valid
 */
export const isValidAmount = (value) => {
  if (value === "" || value === null || value === undefined) return true;
  const num = parseFloat(value);
  return (
    VALIDATION_REGEX.AMOUNT.test(value) &&
    num >= VALIDATION_RULES.AMOUNT.MIN &&
    num <= VALIDATION_RULES.AMOUNT.MAX
  );
};

/**
 * Validate if a file size is within limits
 * @param {number} fileSize - File size in bytes
 * @returns {boolean} - True if valid
 */
export const isValidFileSize = (fileSize) => {
  return fileSize <= VALIDATION_RULES.FILE_SIZE.MAX;
};

/**
 * Validate if reject quantity is within PO quantity
 * @param {number} rejectQty - Reject quantity
 * @param {number} poQty - PO quantity
 * @returns {boolean} - True if valid
 */
export const isRejectQtyValid = (rejectQty, poQty) => {
  return parseFloat(rejectQty) <= parseFloat(poQty);
};

/**
 * Check if all required fields are filled
 * @param {Object} fields - Object containing field values
 * @param {Array} requiredFields - Array of required field keys
 * @returns {Object} - Object with validation results and errors
 */
export const validateRequiredFields = (fields, requiredFields) => {
  const errors = {};
  let isValid = true;

  requiredFields.forEach((field) => {
    if (!fields[field] || fields[field] === "" || fields[field] === null) {
      errors[field] = GRN_ERROR_MESSAGES.REQUIRED_FIELD;
      isValid = false;
    }
  });

  return { isValid, errors };
};

/**
 * Format error message for charge already added
 * @param {string} chargeType - Name of the charge type
 * @returns {string} - Formatted error message
 */
export const getChargeAlreadyAddedMessage = (chargeType) => {
  return `${chargeType}${GRN_ERROR_MESSAGES.CHARGE_ALREADY_ADDED}`;
};
